import React from 'react';
import styled from 'styled-components';
import RedirectChain from './RedirectChain';
import { httpStatusChip } from './Pills';
import { indexabilityText } from './indexability';

const jsdiff = require('diff');

export const LABEL = {
    crawlStatus:           'Status',
    indexability:          'Indexability',
    redirectChainElements: 'Redirect',
    title:                 'Title',
    metaDescriptions:      'Meta',
    h1s:                   'H1',
    h2s:                   'H2',
    canonicals:            'Canonical',
};

export const CONTENT_KEYS = new Set(['title', 'metaDescriptions', 'h1s', 'h2s', 'canonicals']);

export { httpStatusChip } from './Pills';

function toStr(val) {
    if (val == null) return '';
    if (Array.isArray(val)) return val.map(v => {
        if (v == null) return '';
        if (typeof v === 'object') return [v.httpStatus, v.destinationURI].filter(Boolean).join(' → ');
        return String(v);
    }).join('\n');
    return String(val);
}

function valueText(key, val) {
    if (key === 'indexability') return indexabilityText(val) ?? '';
    return toStr(val);
}

// Status-ish values are shown as plain before/after cells — word-diffing
// "Indexable" against "Non-Indexable: Canonicalized" is noise, not signal.
const PLAIN_ROW_KEYS = new Set(['crawlStatus', 'indexability']);


export default function Diff({ changedKeys, beforeValues, afterValues, entryCrawlStatus, prevLabel = 'Before', currLabel = 'After' }) {
    const keys = changedKeys?.length > 0 ? changedKeys : Object.keys(LABEL);
    return (
        <DiffTable>
            <DiffHead>
                <DiffHeadCell $label />
                <DiffHeadCell $before>{prevLabel}</DiffHeadCell>
                <DiffHeadCell>{currLabel}</DiffHeadCell>
            </DiffHead>
            {keys.map(key => {
                if (key === 'redirectChainElements') {
                    const before = beforeValues?.[key];
                    const after  = afterValues?.[key];
                    if (before == null && after == null) return null;
                    return (
                        <DiffBodyRow key={key} $changed>
                            <DiffBodyLabel>{LABEL[key] || key}</DiffBodyLabel>
                            <DiffBodyCell $before><RedirectChain elements={before} crawlStatus={beforeValues?.crawlStatus} /></DiffBodyCell>
                            <DiffBodyCell><RedirectChain elements={after} crawlStatus={afterValues?.crawlStatus || entryCrawlStatus} /></DiffBodyCell>
                        </DiffBodyRow>
                    );
                }
                const oldVal = valueText(key, beforeValues?.[key]);
                const newVal = key === 'crawlStatus'
                    ? (valueText(key, afterValues?.[key]) || entryCrawlStatus || '')
                    : valueText(key, afterValues?.[key]);
                if (!oldVal && !newVal) return null;
                if (PLAIN_ROW_KEYS.has(key)) {
                    return (
                        <DiffBodyRow key={key} $changed={oldVal !== newVal}>
                            <DiffBodyLabel>{LABEL[key] || key}</DiffBodyLabel>
                            <DiffBodyCell $before>{oldVal || <DiffMuted>—</DiffMuted>}</DiffBodyCell>
                            <DiffBodyCell>{newVal || <DiffMuted>—</DiffMuted>}</DiffBodyCell>
                        </DiffBodyRow>
                    );
                }
                const parts   = jsdiff.diffWords(oldVal, newVal, { ignoreWhitespace: true });
                const changed = parts.some(p => p.added || p.removed);
                return (
                    <DiffBodyRow key={key} $changed={changed}>
                        <DiffBodyLabel>{LABEL[key] || key}</DiffBodyLabel>
                        <DiffBodyCell $before>
                            {parts.filter(p => !p.added).map((p, i) => (
                                <DiffSpan key={i} $removed={p.removed}>{p.value}</DiffSpan>
                            ))}
                            {!oldVal && <DiffMuted>—</DiffMuted>}
                        </DiffBodyCell>
                        <DiffBodyCell>
                            {parts.filter(p => !p.removed).map((p, i) => (
                                <DiffSpan key={i} $added={p.added}>{p.value}</DiffSpan>
                            ))}
                            {!newVal && <DiffMuted>—</DiffMuted>}
                        </DiffBodyCell>
                    </DiffBodyRow>
                );
            })}
        </DiffTable>
    );
}

const DiffTable = styled.div`
    display: flex; flex-direction: column;
    font-family: "Inter", system-ui, sans-serif;
    min-width: 420px;
`;

const DiffHead = styled.div`
    display: grid;
    grid-template-columns: 95px 1fr 1fr;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
`;

const DiffHeadCell = styled.div`
    padding: 6px 10px;
    font-size: 0.65rem; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.5px; color: #94a3b8;
    display: flex; flex-direction: column; gap: 1px;
    ${({ $before }) => $before && 'border-left: 1px solid #e2e8f0;'}
    ${({ $label }) => !$label && 'border-left: 1px solid #e2e8f0;'}
`;


const DiffBodyRow = styled.div`
    display: grid;
    grid-template-columns: 95px 1fr 1fr;
    border-bottom: 1px solid #f8fafc;
    background: ${({ $changed }) => $changed ? '#fff' : '#fafafa'};
`;

const DiffBodyLabel = styled.div`
    padding: 8px 10px;
    font-size: 0.68rem; font-weight: 700; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.4px;
    padding-top: 10px;
`;

const DiffBodyCell = styled.div`
    padding: 8px 10px;
    font-size: 0.75rem; color: #0f172a;
    border-left: 1px solid #e2e8f0;
    word-break: break-word; line-height: 1.6;
    white-space: pre-wrap;
    background: ${({ $before }) => $before ? '#f8fafc' : '#eef2ff'};
`;

const DiffMuted = styled.span`
    color: #94a3b8; font-style: italic;
`;

const DiffSpan = styled.span`
    background: ${({ $added, $removed }) => $added ? '#bbf7d0' : $removed ? '#fecaca' : 'transparent'};
    ${({ $removed }) => $removed && 'text-decoration: line-through; opacity: 0.7;'}
    border-radius: 2px;
`;

